import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { syncButtonNameFromRow } from 'directives/cs-capture-cover/row-name-source';

@Component({
  selector: 'cs-checkbox',
  templateUrl: 'cs-checkbox.html',
  host: { 'class': 'checkbox' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CsCheckboxComponent {
  @Input() checked: boolean = false;
  @Output() checkedChange = new EventEmitter<boolean>();

  @Input() disabled: boolean;

  /**
   * What a screen reader announces for this checkbox, when the row it sits in is not the right
   * name.
   *
   * The checkbox draws a box and nothing else, so the words that say what it is for are always
   * outside the component. Without this input the inner `<button>` takes its name from the
   * enclosing row's text through `aria-labelledby` (see `syncButtonNameFromRow`). Give it a name
   * here when the checkbox has no such row — a checkbox in a grid of dates, say, where the date
   * sits in a cell of its own beside other checkboxes.
   */
  @Input() ariaLabel?: string;

  ngAfterViewChecked() {
    // the inner <button> is empty, so without an ariaLabel it takes its name from the row's text
    syncButtonNameFromRow(this.elementRef, this.ariaLabel);
  }

  ngOnInit() {
    this.refreshSelection();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.checked) {
      this.refreshSelection();
    }
  }

  refreshSelection() {
    const element = this.elementRef.nativeElement as HTMLElement;
    if (this.checked) {
      element.classList.add('checkbox-checked');
    } else {
      element.classList.remove('checkbox-checked');
    }
  }

  handleClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.checked = !this.checked;
    this.checkedChange.emit(this.checked);
    this.refreshSelection();
  }

  constructor(public elementRef: ElementRef) { }
}
